import * as React from "react"
import { SMSTemplatedType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { getZoneDropdown } from "@/lib/api/billing"
import { editSMSTemplatedMessage } from "@/lib/api/templated-messages"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { ComboboxSearch } from "../combobox-search"
import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  zone_id: z.coerce.number().min(1, "Zone is required"),
  due_reminder_type: z.string().min(1, "Due reminder type is required"),
  subject: z.string().min(1, "Subject  is required"),
  header: z.string().min(1, "Header  is required"),
  body: z.string().min(1, "Body  is required"),
  footer: z.string().min(1, "Footer is required"),
})

export default function EditSMSForm({
  smsTemplatedMessage,
  setShowSMSTemplatedModal,
}: {
  smsTemplatedMessage?: SMSTemplatedType
  setShowSMSTemplatedModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [zoneId, setZoneId] = React.useState<number | undefined>()

  const results = useQueries({
    queries: [
      {
        queryKey: ["zones"],
        queryFn: getZoneDropdown,
      },
    ],
  })

  const [zones] = results.map((result) => result.data || [])
  console.log(zones)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_id: smsTemplatedMessage?.zone_id,
      due_reminder_type: smsTemplatedMessage?.due_reminder_type,
      subject: smsTemplatedMessage?.subject,
      header: smsTemplatedMessage?.header,
      body: smsTemplatedMessage?.body,
      footer: smsTemplatedMessage?.footer,
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: SMSTemplatedType) => {
      if (smsTemplatedMessage?.id) {
        await editSMSTemplatedMessage(smsTemplatedMessage?.id, data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smsTemplatedMessages"] })
      toast({
        description: "SMS templated message updated successfully.",
      })
      /* navigate({ to: "/message-templates/sms" }) */
      setShowSMSTemplatedModal(false)
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    console.log(values)
    mutation.mutate(values as SMSTemplatedType)
  }

  /*console.log("Zones:", zones)*/
  console.log("smsTemplatedMessage.zone_id:", smsTemplatedMessage)

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/message-templates/sms",
            label: "SMS Templated",
          },
        ]}
      >
        Edit SMS Templated
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="zone_id"
              render={({ field }) => (
                <FormItem className="flex flex-col justify-center">
                  <FormLabel>
                    Zone<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <ComboboxSearch
                      field={field}
                      options={zones}
                      placeholder="Select zone"
                      onSelectId={(id?: string | number) =>
                        setZoneId(id as number)
                      }
                      idAsValue
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Subject<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="header"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Header<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Body<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={5} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="footer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Footer<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowSMSTemplatedModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              disabled={mutation.isPending}
              type="submit"
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
